// User & Auth Types
export enum UserRole {
  EMPLOYEE = 'EMPLOYEE',
  LINE_MANAGER = 'LINE_MANAGER',
  SKIP_MANAGER = 'SKIP_MANAGER',
  HR = 'HR',
  LEADER = 'LEADER',
  ADMIN = 'ADMIN',
}

export interface User {
  id: number;
  email: string;
  role: UserRole;
  employeeId?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AuthUser extends User {
  employee?: Employee;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
}

// Department Types
export enum DepartmentStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export interface Department {
  id: number;
  code: string;
  name: string;
  parentId?: number;
  headEmployeeId?: number;
  status: DepartmentStatus;
  createdAt: string;
  updatedAt: string;
}

export interface DepartmentNode extends Department {
  children?: DepartmentNode[];
}

// Employee Types
export enum EmployeeStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export interface Employee {
  id: number;
  employeeCode: string;
  fullName: string;
  email: string;
  departmentId: number;
  managerId?: number;
  status: EmployeeStatus;
  joinDate: string;
  createdAt: string;
  updatedAt: string;
}

export interface EmployeeDetail extends Employee {
  department?: Department;
  manager?: Employee;
  departmentPath?: Department[];
}

// Leave Request Types
export enum LeaveRequestStatus {
  DRAFT = 'DRAFT',
  PENDING = 'PENDING',
  APPROVED_L1 = 'APPROVED_L1',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  CANCELLED = 'CANCELLED',
}

export enum HalfDayType {
  NONE = 'NONE',
  MORNING = 'MORNING',
  AFTERNOON = 'AFTERNOON',
}

export interface LeaveType {
  id: number;
  code: string;
  name: string;
  isPaid: boolean;
  requiresApproval: boolean;
  status: 'ACTIVE' | 'INACTIVE';
}

export interface LeaveRequest {
  id: number;
  employeeId: number;
  leaveTypeId: number;
  startDate: string;
  endDate: string;
  halfDayType: HalfDayType;
  totalDays: number;
  reason: string;
  status: LeaveRequestStatus;
  isLateSubmission: boolean | number; // MySQL returns 0 or 1
  isPaid?: boolean | number | null; // MySQL returns 0 or 1
  submittedAt?: string;
  lineManagerId?: number;
  lineManagerApprovedAt?: string;
  skipManagerId?: number;
  skipManagerApprovedAt?: string;
  rejectionReason?: string;
  createdAt: string;
  updatedAt: string;
}

export interface LeaveRequestDetail extends LeaveRequest {
  employee?: Employee;
  leaveType?: LeaveType;
  lineManager?: User;
  skipManager?: User;
}

export interface LeaveBalance {
  id: number;
  employeeId: number;
  year: number;
  accruedDays: number;
  usedDays: number;
  remainingDays: number;
}

// Late/Early Request Types
export enum LateEarlyRequestStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

export enum LateEarlyType {
  LATE = 'LATE',
  EARLY = 'EARLY',
}

export interface LateEarlyRequest {
  id: number;
  employeeId: number;
  requestDate: string;
  type: LateEarlyType;
  minutes: number;
  reason: string;
  status: LateEarlyRequestStatus;
  approvedByUserId?: number;
  approvedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface LateEarlyQuota {
  id: number;
  employeeId: number;
  yearMonth: string;
  quotaMinutes: number;
  usedMinutes: number;
  isOverQuota: boolean;
}

// Pagination Types
export interface PaginatedResponse<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
}

// API Error Types
export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, any>;
}
